import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../context/OnboardingContext';

const Congratulations: React.FC = () => {
  const navigate = useNavigate();
  const { setCurrentStep } = useOnboarding();
  const [showContent, setShowContent] = useState(false);
  
  useEffect(() => {
    // Animación de entrada
    setTimeout(() => setShowContent(true), 100);
  }, []);
  
  // Manejar el clic en el botón de aceptar
  const handleAccept = () => {
    setCurrentStep(9); // Completar el onboarding
    navigate('/'); // Navegar al dashboard o página principal
  };
  
  return (
    <div className="h-screen bg-gradient-to-br from-xervix-bg-primary via-xervix-bg-secondary to-xervix-bg-primary dark:from-xervix-bg-primary-dark dark:via-xervix-bg-secondary-dark dark:to-xervix-bg-primary-dark flex items-center justify-center p-4 relative overflow-hidden">
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Círculos decorativos animados */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-xervix-primary opacity-10 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-xervix-primary opacity-10 rounded-full animate-pulse delay-75"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-xervix-primary opacity-10 rounded-full animate-pulse delay-150"></div>
        
        {/* Confeti animado */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-xervix-primary rounded-full animate-confetti"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-${Math.random() * 20}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* Contenedor principal */}
      <div className={`relative z-10 bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark rounded-2xl shadow-2xl w-full max-w-2xl px-8 py-6 text-center transform transition-all duration-700 ${showContent ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
        
        {/* Icono de éxito grande con animación */}
        <div className="flex justify-center mb-4">
          <div className="relative">
            {/* Círculo exterior pulsante */}
            <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-20"></div>
            {/* Círculo principal */}
            <div className="relative bg-gradient-to-br from-green-400 to-green-600 rounded-full p-6 shadow-lg transform hover:scale-110 transition-transform duration-300">
              <svg className="w-16 h-16 text-white animate-bounce-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Título principal con gradiente */}
        <h1 className="text-4xl md:text-5xl font-extrabold mb-3 bg-gradient-to-r from-xervix-primary via-xervix-primary to-xervix-primary bg-clip-text text-transparent animate-gradient">
          ¡FELICIDADES!
        </h1>
        
        {/* Línea decorativa */}
        <div className="flex justify-center mb-4">
          <div className="h-1 w-24 bg-gradient-to-r from-transparent via-xervix-primary to-transparent rounded-full"></div>
        </div>
        
        {/* Subtítulo */}
        <h2 className="text-lg md:text-xl font-bold text-xervix-text-primary dark:text-xervix-text-primary-dark mb-4">
          ¡Te has registrado exitosamente en XERVIX!
        </h2>
        
        {/* Mensaje de bienvenida */}
        <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-xl p-4 mb-4">
          <p className="text-sm text-xervix-text-secondary dark:text-xervix-text-secondary-dark leading-relaxed">
            🎉 <span className="font-semibold">¡Bienvenido a nuestra comunidad!</span> Estamos emocionados de tenerte con nosotros.
          </p>
          <p className="text-sm text-xervix-text-secondary dark:text-xervix-text-secondary-dark leading-relaxed mt-2">
            Ahora formas parte de una red donde conectamos empresas como la tuya con clientes que buscan 
            <span className="font-semibold text-xervix-primary"> soluciones confiables y de calidad</span>.
          </p>
        </div>

        {/* Características destacadas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          <div className="bg-xervix-bg-primary dark:bg-xervix-bg-primary-dark rounded-lg p-3 transform hover:scale-105 transition-transform">
            <div className="text-2xl mb-1">🚀</div>
            <p className="text-xs font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark">Comienza a recibir leads</p>
          </div>
          <div className="bg-xervix-bg-primary dark:bg-xervix-bg-primary-dark rounded-lg p-3 transform hover:scale-105 transition-transform">
            <div className="text-2xl mb-1">💼</div>
            <p className="text-xs font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark">Gestiona tus proyectos</p>
          </div>
          <div className="bg-xervix-bg-primary dark:bg-xervix-bg-primary-dark rounded-lg p-3 transform hover:scale-105 transition-transform">
            <div className="text-2xl mb-1">⭐</div>
            <p className="text-xs font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark">Construye tu reputación</p>
          </div>
        </div>
        
        {/* Botón de aceptar mejorado */}
        <button
          onClick={handleAccept}
          className="group relative px-10 py-3 rounded-xl font-bold text-base text-white bg-xervix-primary hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
        >
          <span className="relative z-10">Ir al Dashboard</span>
          <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
        </button>

        {/* Mensaje adicional */}
        <p className="text-xs text-xervix-text-secondary dark:text-xervix-text-secondary-dark mt-4">
          Tu perfil está siendo revisado. Te notificaremos cuando esté aprobado.
        </p>
      </div>

      {/* Estilos personalizados para animaciones */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        @keyframes gradient {
          0%, 100% {
            background-size: 200% 200%;
            background-position: left center;
          }
          50% {
            background-size: 200% 200%;
            background-position: right center;
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-confetti {
          animation: confetti linear infinite;
        }
        
        .animate-gradient {
          animation: gradient 3s ease infinite;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        
        .delay-75 {
          animation-delay: 0.75s;
        }
        
        .delay-150 {
          animation-delay: 1.5s;
        }
      `}</style>
    </div>
  );
};

export default Congratulations;
